import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ServiceSelectionEnum } from '../../../../../../core/enums/applications/service-selection-enum';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { ApplicationService } from '../../../../shared/services/application/application.service';
import { ApplicationPageFacadeService } from '../../../shared/services/application-page-facade/application-page-facade.service';
import { SubmitToEwaFormOptionComponent } from '../shared/components/submit-to-ewa-form-option/submit-to-ewa-form-option.component';
import { SubmitToEwaFormSubmissionComponent } from '../shared/components/submit-to-ewa-form-submission/submit-to-ewa-form-submission.component';
import { SubmitToEwaPageBaseComponent } from '../shared/components/submit-to-ewa-page-base/submit-to-ewa-page-base.component';
import { EwaSubmissionResponse } from '../shared/models/ewa-submission-response';
import { SubmitToEwaDetailsResponse } from '../shared/models/submit-to-ewa-details-response';
import { SubmitToEwaFormOption } from '../shared/models/submit-to-ewa-form-option';
import { SubmitToEwaFormService } from '../shared/services/submit-to-ewa-form.service';
import { CoordinationIndustrialBusinessFormOptionConstants } from './constants/coordination-industrial-business-form-option-constants';
import { CoordinationIndustrialBusinessSubmitToEwaFormModel } from './models/coordination-industrial-business-submit-to-ewa-form-model';
import { CoordinationIndustrialBusinessSubmitToEwaRequest } from './models/coordination-industrial-business-submit-to-ewa-request';

@Component({
    selector: 'app-coordination-industrial-business-submit-to-ewa',
    templateUrl:
        './coordination-industrial-business-submit-to-ewa.component.html',
    styleUrl: '../shared/styles/submit-to-ewa.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatInputModule,
        MatButtonModule,
        SubmitToEwaFormOptionComponent,
        SubmitToEwaFormSubmissionComponent,
    ],
})
export class CoordinationIndustrialBusinessSubmitToEwaComponent extends SubmitToEwaPageBaseComponent<
    CoordinationIndustrialBusinessSubmitToEwaRequest,
    CoordinationIndustrialBusinessSubmitToEwaFormModel
> {
    readonly formServiceSelectionOptions: SubmitToEwaFormOption[] =
        this.createFormOptions();
    selectedFormOption: SubmitToEwaFormOption | null = null;

    constructor(
        protected override readonly route: ActivatedRoute,
        protected override readonly router: Router,
        protected override readonly applicationPageFacadeService: ApplicationPageFacadeService,
        private readonly submitToEwaService: SubmitToEwaFormService,
        private readonly applicationService: ApplicationService,
    ) {
        super(route, router, applicationPageFacadeService);
    }

    override form = new FormGroup<
        FormGroupType<CoordinationIndustrialBusinessSubmitToEwaFormModel>
    >({});

    /**
     * Determines if the user is allowed to do a final submission.
     * @returns whether the user is allowed to do a final submission
     */
    enableSubmission(): boolean {
        return Boolean(
            this.selectedFormOption && !this.applicationState?.handoverToEwa,
        );
    }

    /**
     * Submits the form to the EWA. Will do a modal popup to confirm the submission.
     * Then, will submit the form and redirect you to the dashboard.
     */
    handoverToEwaSubmission(): void {
        if (this.enableSubmission()) {
            this.submitToEwaService.handoverToEwaSubmission(
                () => this.saveFormDataToApi(this.generateRequestObject(true)),
                () => {
                    /**
                     * This is needed to prevent the guard from blocking the navigation
                     * since the submission has already been made and the user is being redirected.
                     */
                    this.hasChanges = false;
                },
            );
        } else {
            this.form.markAllAsTouched();
        }
    }

    /**
     * Handles the service selection option being clicked. Updates the selected form option.
     * @param formServiceSelectionOption the selected form option card.
     */
    handleServiceSelection(
        formServiceSelectionOption: SubmitToEwaFormOption,
    ): void {
        this.selectedFormOption = formServiceSelectionOption;
    }

    protected override bindForm(response: SubmitToEwaDetailsResponse): void {
        // Assigning the default selected form option upon receiving the response.
        this.loadDefaultSelectedFormOption(response.serviceSelection);
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<SubmitToEwaDetailsResponse> {
        return this.applicationService.getDataForFinalSubmission(applicationId);
    }

    protected override saveFormDataToApi(
        request: CoordinationIndustrialBusinessSubmitToEwaRequest,
    ): Observable<EwaSubmissionResponse> {
        return this.applicationService.finalSubmissionToEwa(request);
    }

    protected override generateRequestObject(
        isFinalSubmission: boolean = false,
    ): CoordinationIndustrialBusinessSubmitToEwaRequest {
        const formValue = this.form
            .value as Required<CoordinationIndustrialBusinessSubmitToEwaRequest>;

        const baseRequest: CoordinationIndustrialBusinessSubmitToEwaRequest = {
            ...formValue,
            submitToEwa: isFinalSubmission,
            applicationId: this.applicationId!,
        };

        if (isFinalSubmission) {
            baseRequest.serviceSelection = this.selectedFormOption!.service;
        }

        return baseRequest;
    }

    /**
     * Loads the default selected form option based on the service selection value from the API.
     */
    private loadDefaultSelectedFormOption(
        serviceSelection: ServiceSelectionEnum | null = CoordinationIndustrialBusinessFormOptionConstants
            .useEwaSelectsFrequenciesFormOption.service,
    ): void {
        if (
            serviceSelection ==
            CoordinationIndustrialBusinessFormOptionConstants
                .useFrequenciesSelfSelectionFormOption.service
        ) {
            this.handleServiceSelection(
                CoordinationIndustrialBusinessFormOptionConstants.useFrequenciesSelfSelectionFormOption,
            );
        } else {
            this.handleServiceSelection(
                CoordinationIndustrialBusinessFormOptionConstants.useEwaSelectsFrequenciesFormOption,
            );
        }
    }

    /**
     * Creates the form options for the form option cards.
     * @returns List of form options for the form option cards.
     */
    private createFormOptions(): SubmitToEwaFormOption[] {
        return CoordinationIndustrialBusinessFormOptionConstants.formOptions();
    }
}
